import React from 'react';
import {
  FileText,
  Server,
  UserCheck,
  MessageSquare,
  ShieldAlert,
  Camera,
  Smartphone,
  Globe,
  AlertTriangle,
  Scale,
  LogOut,
  RefreshCw,
  HelpCircle,
  Users,
  ArrowLeftRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TermsOfService() {
  const sections = [
    {
      icon: FileText,
      title: '1. Overview & Acceptance',
      content: (
        <>
          <p className="mb-3">
            These Terms of Service ("Terms") govern your access to and use of the ZenRent platform
            ("Service"), operated by ZenRent ("we," "us," or "our"). ZenRent is a property management
            and maintenance coordination platform for landlords, property managers, and tenants.
          </p>
          <p className="mb-3">
            By creating an account, accessing, or using ZenRent, you acknowledge that you have read,
            understood, and agree to be bound by these Terms and our{' '}
            <Link to="/privacy" className="text-emerald-600 hover:underline">Privacy Policy</Link>.
            If you do not agree to these Terms, you may not use the Service.
          </p>
        </>
      ),
    },
    {
      icon: Server,
      title: '2. Service Description',
      content: (
        <>
          <p className="mb-3">ZenRent provides the following capabilities through its platform:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Maintenance Coordination:</strong> Submit, track, and manage property maintenance requests end-to-end</li>
            <li><strong>Tenant & Landlord Communication:</strong> In-app and SMS-based messaging between tenants and property managers within a rental relationship, routed through dedicated ZenRent phone numbers</li>
            <li><strong>Scheduling & Appointments:</strong> Coordinate service appointments and track status</li>
            <li><strong>AI-Assisted Diagnosis:</strong> AI-powered tools that provide preliminary maintenance assessments and recommendations</li>
            <li><strong>Lease Management:</strong> Create, distribute, and e-sign lease agreements</li>
            <li><strong>Notifications:</strong> Email and optional SMS notifications for account activity and rental operations</li>
          </ul>
          <p className="mt-4 text-gray-600">
            <strong>Important:</strong> AI-assisted diagnosis features are provided for informational purposes
            only and do not constitute professional advice (including but not limited to engineering,
            plumbing, electrical, or legal advice). You are solely responsible for any decisions made based
            on AI-generated suggestions. Always consult qualified professionals for significant maintenance
            or repair decisions.
          </p>
        </>
      ),
    },
    {
      icon: UserCheck,
      title: '3. Eligibility & Accounts',
      content: (
        <>
          <p className="mb-3">To use ZenRent, you must meet the following requirements:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Age Requirement:</strong> You must be at least 18 years of age to create an account and use the Service</li>
            <li><strong>Accurate Information:</strong> You agree to provide accurate, current, and complete information during registration and to keep your account information up to date</li>
            <li><strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account</li>
            <li><strong>One Person Per Account:</strong> Each account is for a single individual. You may not share your account credentials with others</li>
          </ul>
          <p className="mt-4 text-gray-600">
            You must notify us immediately at{' '}
            <a href="mailto:support@zenrent.app" className="text-emerald-600 hover:underline">support@zenrent.app</a>{' '}
            if you suspect any unauthorized use of your account.
          </p>
        </>
      ),
    },
    {
      icon: MessageSquare,
      title: '4. User Roles & Communications',
      content: (
        <>
          <p className="mb-3">ZenRent supports two primary user roles:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Landlords / Property Managers:</strong> Users who manage rental properties, handle maintenance requests, and communicate with tenants</li>
            <li><strong>Tenants:</strong> Users who occupy rental properties, submit maintenance requests, and communicate with their property manager</li>
          </ul>
          <p className="mt-4 mb-3">In-app messaging is provided for communication between parties in a rental relationship. By using messaging features, you agree to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Use messaging only for legitimate rental and property management purposes</li>
            <li>Not use messaging for spam, harassment, threats, or abusive conduct</li>
            <li>Not use messaging for unsolicited marketing, advertising, or promotional blasts</li>
            <li>Communicate respectfully and in good faith</li>
          </ul>
        </>
      ),
    },
    {
      icon: ShieldAlert,
      title: '5. Acceptable Use & Prohibited Activities',
      content: (
        <>
          <p className="mb-3">You agree not to use ZenRent to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Violate any applicable law, regulation, or ordinance</li>
            <li>Harass, threaten, intimidate, or discriminate against any person</li>
            <li>Send spam, unsolicited messages, or marketing blasts via ZenRent communications</li>
            <li>Engage in fraud, misrepresentation, or deceptive practices</li>
            <li>Attempt to gain unauthorized access to the Service, other accounts, or related systems</li>
            <li>Scrape, crawl, or use automated means to extract data from ZenRent</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Upload malicious code, viruses, or any content designed to disrupt the Service</li>
            <li>Interfere with or disrupt the integrity or performance of the Service</li>
          </ul>
          <p className="mt-4 text-gray-600">
            We reserve the right to investigate and take appropriate action against anyone who violates
            these prohibitions, including suspending or terminating their account.
          </p>
        </>
      ),
    },
    {
      icon: Camera,
      title: '6. User Content & Permissions',
      content: (
        <>
          <p className="mb-3">
            You may submit text, photographs (such as maintenance request images), descriptions, and
            other content ("User Content") through ZenRent.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Ownership:</strong> You retain ownership of your User Content</li>
            <li><strong>License Grant:</strong> By submitting User Content, you grant ZenRent a limited, non-exclusive, royalty-free license to store, process, display, and transmit your content solely as necessary to operate and provide the Service</li>
            <li><strong>Content Standards:</strong> You represent that your User Content does not violate any law or infringe upon any third-party rights</li>
            <li><strong>Removal:</strong> ZenRent reserves the right to remove any User Content that violates these Terms or is otherwise objectionable, at our sole discretion</li>
          </ul>
        </>
      ),
    },
    {
      icon: Smartphone,
      title: '7. SMS & Text Messaging Terms',
      content: (
        <>
          <p className="mb-3">
            <strong>ZenRent</strong> offers two optional SMS text messaging programs. By opting in to
            either or both programs, you agree to the following terms:
          </p>

          <p className="mb-2 font-semibold text-gray-800">Program Names</p>
          <ul className="list-disc list-inside space-y-1 text-gray-600 mb-4">
            <li><strong>ZenRent Transactional Alerts</strong></li>
            <li><strong>ZenRent Rental Communications (Proxy)</strong></li>
          </ul>

          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>
              <strong>Types of Messages:</strong>
              <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                <li>
                  <strong>Transactional Alerts:</strong> Security alerts, important account notices,
                  verification codes (if applicable), and operational notifications related to your
                  ZenRent account. ZenRent does not send marketing, promotional, or cold-outreach
                  messages via this program.
                </li>
                <li>
                  <strong>Rental Communications (Proxy):</strong> Messages exchanged between landlords/property
                  managers and tenants through ZenRent using dedicated proxy phone numbers, including
                  maintenance coordination, scheduling, and rental-related communications. See Section 8
                  for details on how proxy messaging works.
                </li>
              </ul>
            </li>
            <li>
              <strong>Message Frequency:</strong> Message frequency varies based on your account
              activity and rental communications. You will only receive messages related to actions on
              your account or communications initiated within your rental relationship.
            </li>
            <li>
              <strong>Consent / How to Opt In:</strong> You opt in to each SMS program separately by
              providing your phone number and checking the corresponding consent checkbox during
              account signup or in your account settings. Each consent is independent—you may opt in to
              Transactional Alerts, Rental Communications (Proxy), both, or neither. Your consent is
              provided voluntarily through an explicit, affirmative action on the ZenRent website.
            </li>
            <li>
              <strong>Tenant Consent:</strong> Tenants opt in by providing their phone number and
              checking the appropriate consent checkbox(es) during account signup or in their account
              settings. By opting in to Transactional Alerts, tenants agree to receive account and
              security-related messages. By opting in to Rental Communications, tenants agree to send
              and receive rental-related messages exchanged with their landlord or property manager
              through a dedicated ZenRent proxy phone number.
            </li>
            <li>
              <strong>Landlord/Property Manager Consent:</strong> Landlords and property managers opt
              in and consent to send and receive SMS through the ZenRent platform by providing their
              phone number and checking the appropriate consent checkbox(es) during signup or in their
              account settings. By opting in, landlords and property managers agree to use SMS messaging
              only with tenants with whom they have a current, legitimate rental relationship, and only
              for legitimate rental and property management purposes.
            </li>
            <li>
              <strong>Opt-Out:</strong> You can stop receiving SMS messages at any time by replying{' '}
              <strong>STOP</strong> to any message from ZenRent. You may also update your SMS preferences
              in your <Link to="/dashboard" className="text-emerald-600 hover:underline">account settings</Link>.
              After opting out, you will receive a confirmation message and no further SMS messages
              will be sent for that program. You may re-enable SMS at any time in your account settings.
            </li>
            <li>
              <strong>Help:</strong> Reply <strong>HELP</strong> to any message for assistance, or
              contact us at{' '}
              <a href="mailto:support@zenrent.app" className="text-emerald-600 hover:underline">
                support@zenrent.app
              </a>.
            </li>
            <li>
              <strong>Message & Data Rates:</strong> Message and data rates may apply depending on your
              mobile carrier and plan. ZenRent is not responsible for any charges from your carrier.
            </li>
            <li>
              <strong>Consent Not Required for Purchase:</strong> Consent to receive SMS messages is
              not a condition of purchasing or using ZenRent. You may use ZenRent without opting in to
              either SMS program.
            </li>
            <li>
              <strong>Acceptable Use of SMS:</strong> You agree not to use ZenRent's SMS messaging
              features to send harassing, threatening, abusive, or discriminatory messages; unsolicited
              marketing or spam; or any content that violates applicable law. SMS messaging must be used
              only for legitimate rental-related communications within an established landlord-tenant
              relationship.
            </li>
            <li>
              <strong>Messaging Suspension:</strong> ZenRent reserves the right to suspend or disable
              SMS messaging for any account that violates these Terms, engages in abusive messaging
              practices, or where suspension is required for regulatory or carrier compliance. We will
              notify you if your messaging privileges are suspended.
            </li>
            <li>
              <strong>Delivery Disclaimer:</strong> Message delivery is subject to effective
              transmission from your network carrier. ZenRent and mobile carriers are not liable for
              delayed or undelivered messages. SMS may not be available on all carriers or devices.
            </li>
            <li>
              <strong>Supported Carriers:</strong> SMS messages are supported on most major U.S.
              carriers, including AT&T, Verizon, T-Mobile, and others. Carrier support is not
              guaranteed for all networks.
            </li>
          </ul>
          <p className="mt-4 text-gray-600">
            <strong>Contact:</strong> For questions about SMS messaging, contact us at{' '}
            <a href="mailto:support@zenrent.app" className="text-emerald-600 hover:underline">
              support@zenrent.app
            </a>.
          </p>
          <p className="mt-2 text-gray-600">
            For more information about how we handle your data, see our{' '}
            <Link to="/privacy" className="text-emerald-600 hover:underline">Privacy Policy</Link>.
            These SMS terms are part of the ZenRent{' '}
            <Link to="/terms" className="text-emerald-600 hover:underline">Terms of Service</Link>.
          </p>
        </>
      ),
    },
    {
      icon: ArrowLeftRight,
      title: '8. Proxy/Intermediary Messaging',
      content: (
        <>
          <p className="mb-3">
            ZenRent acts as a messaging intermediary between landlords (and their property managers) and
            tenants. To facilitate this communication, ZenRent assigns a dedicated phone number to each
            landlord organization on the platform.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>
              <strong>How It Works:</strong> When a landlord or property manager sends a message through
              the ZenRent platform, it is delivered to the tenant via SMS from the dedicated ZenRent
              number assigned to that organization. When a tenant replies to that number, the message is
              routed back to the landlord or property manager through the ZenRent platform.
            </li>
            <li>
              <strong>Dedicated Numbers:</strong> Each landlord organization is assigned a unique ZenRent
              phone number. Tenants communicate with their landlord by texting this dedicated number. The
              number is used exclusively for rental-related communications facilitated by ZenRent.
            </li>
            <li>
              <strong>No Direct Number Sharing:</strong> ZenRent does not share landlords' or tenants'
              personal phone numbers with the other party. All communications are routed through the
              ZenRent platform and its dedicated numbers.
            </li>
            <li>
              <strong>Platform Facilitation:</strong> ZenRent processes and routes messages but does not
              independently generate message content on behalf of landlords or tenants. The content of
              messages is determined by the sending party, subject to the acceptable use rules in these
              Terms.
            </li>
          </ul>
        </>
      ),
    },
    {
      icon: Users,
      title: '9. User Responsibilities',
      content: (
        <>
          <p className="mb-3">
            All users are expected to use ZenRent responsibly and in compliance with applicable law.
            Landlords and property managers have additional responsibilities when using ZenRent's
            messaging and communication features:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>
              <strong>Lawful Basis:</strong> Landlords must have a current, legitimate rental
              relationship with each tenant they contact through ZenRent. You may not use ZenRent to
              message individuals who are not your tenants or who do not have an active relationship
              with your organization on the platform.
            </li>
            <li>
              <strong>Tenant-Only Messaging:</strong> SMS and in-app messaging features must be used
              only to communicate with your own tenants about matters related to their tenancy, such as
              maintenance coordination, lease matters, scheduling, and account notices.
            </li>
            <li>
              <strong>Respect Opt-Out:</strong> If a tenant opts out of SMS messaging (by replying STOP
              or through their account settings), landlords must respect that choice. Do not attempt to
              circumvent opt-out preferences or pressure tenants to re-subscribe.
            </li>
            <li>
              <strong>No Spam or Marketing:</strong> Landlords may not use ZenRent messaging for
              marketing, advertising, promotional campaigns, or any communications unrelated to the
              rental relationship.
            </li>
            <li>
              <strong>Accurate Information:</strong> Landlords are responsible for ensuring that tenant
              contact information entered into ZenRent is accurate and that tenants have been properly
              invited to the platform.
            </li>
          </ul>
          <p className="mt-4 text-gray-600">
            ZenRent reserves the right to suspend or terminate accounts that violate these
            responsibilities, including restricting messaging privileges where misuse is detected.
          </p>
        </>
      ),
    },
    {
      icon: Globe,
      title: '10. Third-Party Services',
      content: (
        <>
          <p className="mb-3">
            ZenRent integrates with third-party services to provide its functionality:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>SMS Delivery:</strong> Twilio is used for SMS message delivery. Your phone number and message content are shared with Twilio solely for the purpose of delivering messages</li>
            <li><strong>Cloud Infrastructure:</strong> We use industry-standard cloud providers for hosting, storage, and data processing</li>
            <li><strong>E-Signature Services:</strong> Third-party e-signature providers facilitate lease signing</li>
          </ul>
          <p className="mt-4 text-gray-600">
            ZenRent is not responsible for the availability, reliability, or performance of third-party
            services. Third-party services are governed by their own terms and privacy policies.
          </p>
        </>
      ),
    },
    {
      icon: AlertTriangle,
      title: '11. Disclaimers',
      content: (
        <>
          <p className="mb-3">
            The Service is provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis,
            without warranties of any kind, either express or implied, including but not limited to
            implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>We do not guarantee that the Service will be uninterrupted, timely, secure, or error-free</li>
            <li>AI-assisted features are informational only and may not be accurate or complete</li>
            <li>We are not responsible for decisions made based on information provided through the Service</li>
            <li>We do not guarantee the quality, suitability, or availability of any contractors or service providers</li>
          </ul>
        </>
      ),
    },
    {
      icon: Scale,
      title: '12. Limitation of Liability',
      content: (
        <>
          <p className="mb-3">
            To the maximum extent permitted by applicable law, ZenRent and its officers, directors,
            employees, and agents shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages, including but not limited to loss of profits, data,
            or goodwill, arising out of or in connection with your use of the Service.
          </p>
          <p className="text-gray-600">
            In no event shall ZenRent's total cumulative liability to you for all claims arising out
            of or relating to these Terms or your use of the Service exceed the greater of (a) the
            total amount you paid to ZenRent in the twelve (12) months preceding the claim, or
            (b) one hundred U.S. dollars ($100.00).
          </p>
        </>
      ),
    },
    {
      icon: LogOut,
      title: '13. Termination',
      content: (
        <>
          <p className="mb-3">
            We may suspend or terminate your access to ZenRent at any time, with or without notice,
            for conduct that we determine, in our sole discretion, violates these Terms or is harmful
            to other users, us, or third parties, or for any other reason.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Voluntary Termination:</strong> You may stop using the Service at any time. To request account deletion, contact us at <a href="mailto:support@zenrent.app" className="text-emerald-600 hover:underline">support@zenrent.app</a></li>
            <li><strong>Effect of Termination:</strong> Upon termination, your right to use the Service ceases immediately. We may retain certain data as required by law or for legitimate business purposes</li>
          </ul>
        </>
      ),
    },
    {
      icon: RefreshCw,
      title: '14. Changes to These Terms',
      content: (
        <>
          <p className="mb-3">
            We may update these Terms from time to time. When we make changes, we will update the
            "Last updated" date at the top of this page and, where appropriate, notify you via email
            or through the Service.
          </p>
          <p className="text-gray-600">
            Your continued use of ZenRent after updated Terms are posted constitutes your acceptance
            of the changes. If you do not agree to the updated Terms, you must stop using the Service.
          </p>
        </>
      ),
    },
    {
      icon: HelpCircle,
      title: '15. Governing Law & Contact',
      content: (
        <>
          <p className="mb-3">
            These Terms shall be governed by and construed in accordance with the laws of the
            State of Iowa, USA, without regard to its conflict of law provisions.
          </p>
          <p className="mb-3 text-gray-600">
            Any disputes arising out of or relating to these Terms or the Service shall be resolved
            in the state or federal courts located in Iowa, USA.
          </p>
          <p className="mb-3">If you have any questions about these Terms, please contact us:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Email:</strong> <a href="mailto:support@zenrent.app" className="text-emerald-600 hover:underline">support@zenrent.app</a></li>
          </ul>
          <p className="mt-4 text-gray-500 text-sm">
            We aim to respond to all inquiries within 48 business hours.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-semibold text-gray-900">
            Zen<span className="text-emerald-600">Rent</span>
          </Link>
          <Link
            to="/dashboard"
            className="text-emerald-600 hover:text-emerald-700 font-medium transition"
          >
            Back to Dashboard
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms &amp; Conditions</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Please read these Terms of Service carefully before using ZenRent. These Terms govern
            your access to and use of our property management and maintenance coordination platform.
          </p>
          <p className="text-gray-400 text-sm mt-4">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Terms Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <section
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h2>
                  <div className="text-gray-700 leading-relaxed">
                    {section.content}
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>

        {/* Entire Agreement Notice */}
        <section className="mt-8 bg-slate-50 rounded-2xl p-8 border border-slate-100">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Entire Agreement</h2>
          <p className="text-gray-700 leading-relaxed">
            These Terms, together with the{' '}
            <Link to="/privacy" className="text-emerald-600 hover:underline">Privacy Policy</Link>,
            constitute the entire agreement between you and ZenRent regarding your use of the Service
            and supersede all prior agreements, understandings, and communications, whether written or
            oral, relating to the subject matter hereof.
          </p>
        </section>

        {/* Severability */}
        <section className="mt-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Severability</h2>
          <p className="text-gray-700 leading-relaxed">
            If any provision of these Terms is found to be unenforceable or invalid, that provision
            shall be limited or eliminated to the minimum extent necessary so that the remaining
            provisions of these Terms remain in full force and effect.
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-16">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} ZenRent. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-gray-500 hover:text-emerald-600 transition">
                Privacy Policy
              </Link>
              <Link to="/unsubscribe" className="text-gray-500 hover:text-emerald-600 transition">
                Unsubscribe
              </Link>
              <a href="mailto:support@zenrent.app" className="text-gray-500 hover:text-emerald-600 transition">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
